import { useCallback, useEffect, useMemo, useState } from "react";

import qs from "qs";
import color from "color";
import { useQuery } from "@tanstack/react-query";
import { Outlet, useNavigate } from "react-router-dom";
import { useWindowSize } from "react-use";
import { useTranslation } from "react-i18next";

import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";

import Call from "../../assets/icons/Call.svg";

import { MUIHomeSmallButton } from "../Home";

import { client } from "../../bit/board/hooks/useGraphQLClient";
import {
  useCommunicationProvider,
  useLayout,
  useTeamsVideoCallEnabled,
} from "../../lib/hooks/useQueryBoard";
import { useDragScroll } from "../../lib/hooks/useDragScroll";

import Bump from "../../components/Animations/Bump";
import Title from "../../components/Title";
import Curve from "../../components/Animations/PageTransitions/Curve";
import BackButton from "../../components/BackButton";
import { MUTATION_GET_CALLER_ACS_TOKEN_AND_TARGET_TEAMS_USER_IDS } from "../../config/graphql/mutation";

const SCROLL_THRESHOLD_PX = 8;

const CallingMenuScroller = styled("div")({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  width: "100%",
  flex: "1 1 0",
  minHeight: 0,
});

const CallingMenuGrid = styled(Grid)(({ theme }) => ({
  flex: "1 1 auto",
  minHeight: 0,
  overflowY: "auto",
  alignContent: "flex-start",
  padding: "1.5rem 0",
  WebkitOverflowScrolling: "touch",

  "&::-webkit-scrollbar": {
    width: "2rem",
  },

  "&::-webkit-scrollbar-track": {
    background: theme.palette.primary.light,
    borderRadius: "8px",
  },

  "&::-webkit-scrollbar-thumb": {
    background: theme.palette.primary.main,
    borderRadius: "8px",
  },

  "@media screen and (orientation: landscape)": {
    padding: "0 0 4rem",
  },
}));

const ScrollHint = styled("div")(({ theme }) => ({
  position: "absolute",
  left: 0,
  right: 0,
  bottom: 0,
  height: "8rem",
  pointerEvents: "none",
  background: `linear-gradient(to top, ${
    theme.palette.background.default
  }, ${color(theme.palette.background.default).alpha(0).rgb().string()})`,
}));

const useHasMoreBelow = (element: HTMLElement | null, revision: unknown) => {
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    if (!element) {
      return undefined;
    }

    const update = () =>
      setHasMore(
        element.scrollHeight - element.scrollTop - element.clientHeight >
          SCROLL_THRESHOLD_PX,
      );

    update();

    element.addEventListener("scroll", update, { passive: true });

    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);

    observer?.observe(element);

    return () => {
      element.removeEventListener("scroll", update);
      observer?.disconnect();
    };
  }, [element, revision]);

  return hasMore;
};

const CallingMenu = () => {
  const navigate = useNavigate();

  const { t } = useTranslation(["callingMenu"]);

  const { callReception } = useLayout();

  const { width, height } = useWindowSize();

  const orientation = width < height ? "portrait" : "landscape";

  const communicationProvider = useCommunicationProvider();

  const teamsVideoCallEnabled = useTeamsVideoCallEnabled();

  const options = useMemo(
    () => callReception?.callMenu?.options || [],
    [callReception],
  );

  const [scroller, setScroller] = useState<HTMLDivElement | null>(null);

  const hasMoreBelow = useHasMoreBelow(scroller, `${options.length}-${width}`);

  useDragScroll(scroller);

  const receptionistTeamsEmails = useMemo(
    () =>
      options
        .map((option) => option.email)
        .filter((email): email is string => !!email?.trim()),
    [options],
  );

  const { data } = useQuery<{
    getCallerAcsTokenAndTargetTeamsUserIds: CallerAcsTokenAndTargetTeamsUserIdsResponse;
  }>(
    ["getCallerAcsTokenAndTargetTeamsUserIds", receptionistTeamsEmails],
    () => {
      return client.request(
        MUTATION_GET_CALLER_ACS_TOKEN_AND_TARGET_TEAMS_USER_IDS,
        {
          input: {
            emails: receptionistTeamsEmails,
          },
        },
      );
    },
    {
      enabled: communicationProvider === "teams",
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  const handleClickCallReception = useCallback(
    (
      index: number,
      option: {
        label?: string;
        phone?: string;
        email?: string;
      },
    ) => {
      if (communicationProvider === "teams") {
        const callerAcsToken =
          data?.getCallerAcsTokenAndTargetTeamsUserIds?.callerAcsToken;

        const targetTeamsUserIds =
          data?.getCallerAcsTokenAndTargetTeamsUserIds?.targetTeamsUsers
            ?.find((targetTeamsUser) => targetTeamsUser.email === option.email)
            ?.teamsUsers?.map((user) => user.teamsUserId)
            .filter(Boolean)
            .join(",");

        const isGroupCall =
          data?.getCallerAcsTokenAndTargetTeamsUserIds?.targetTeamsUsers?.find(
            (targetTeamsUser) => targetTeamsUser.email === option.email,
          )?.isGroup;

        return navigate(
          `/${
            teamsVideoCallEnabled ? "teams-video" : "calling-menu/teams-audio"
          }-${isGroupCall ? "group-" : ""}call?${qs.stringify({
            callerAcsToken,
            targetTeamsUserIds,
          })}`,
          {
            replace: true,
          },
        );
      }

      return navigate(`/calling-menu/contact/reception-${index}/call`, {
        replace: true,
      });
    },
    [data, communicationProvider],
  );

  return (
    <Curve>
      <div className="d-flex align-items-center flex-column h-100">
        <BackButton to="/" />
        <Title className="page-title home">
          <Typography variant="h1">{t("callingMenu:title")}</Typography>
        </Title>
        <CallingMenuScroller>
          <CallingMenuGrid
            container
            ref={setScroller}
            justifyContent="center"
            alignItems="center"
            rowGap={orientation === "landscape" ? 4 : 0}
          >
            {options.map((option, index) => {
              const isDisabled =
                ((!communicationProvider ||
                  communicationProvider === "twilio") &&
                  (!option.phone || option.phone.trim() === "")) ||
                (communicationProvider === "teams" &&
                  !data?.getCallerAcsTokenAndTargetTeamsUserIds?.targetTeamsUsers?.find(
                    (user) => {
                      if (user.email !== option.email) return false;
                      if (user.isGroup) return true;

                      return !!user.teamsUsers?.[0]?.teamsUserId;
                    },
                  ));

              return (
                // eslint-disable-next-line react/no-array-index-key
                <Bump key={index}>
                  <MUIHomeSmallButton
                    disabled={isDisabled}
                    onClick={() => handleClickCallReception(index, option)}
                  >
                    <Call width="6.5rem" />
                    <Typography variant="h3">{option.label}</Typography>
                  </MUIHomeSmallButton>
                </Bump>
              );
            })}
          </CallingMenuGrid>
          {hasMoreBelow && <ScrollHint />}
        </CallingMenuScroller>
      </div>
      <Outlet />
    </Curve>
  );
};

export default CallingMenu;
